<template>

    <ModalButton @onModalEnter="onModalEnter">
        <template #buttonContent="{ openModal }">
            <Button @click="openModal">
                <TextWithIcon icon="plus">{{ buttonLabel }}</TextWithIcon>
            </Button>
        </template>

        <template #body="{ closeModal }">

            <Box class="mt-4">

                <div class="flex space-x-2 items-end">

                    <div class="w-full">
                        <Label>Search:</Label>
                        <Text v-model="currentFilters.search_text" @keyup.enter="search"></Text>
                    </div>

                    <div class="w-full">
                        <Label>Discount group:</Label>
                        <select v-if="result" v-model="currentFilters.discount_group_id" class="cm-select-input">
                            <template v-for="discountGroupFilter in result.customerDiscountGroupFilter">
                                <option :value="discountGroupFilter.id ?? 'empty'">{{ discountGroupFilter.name ? `${discountGroupFilter.name} (${discountGroupFilter.count})` : `None (${discountGroupFilter.count})` }}</option>
                            </template>

                        </select>
                    </div>

                    <Button class="h-10" @click="search">
                        <TextWithIcon icon="magnifying-glass">Search</TextWithIcon>
                    </Button>

                    <Button class="warning h-10" @click="resetFilters">
                        <fa-icon style="max-width: none" icon="trash"></fa-icon>
                    </Button>

                </div>

            </Box>

            <Box class="mt-4">

                <HasResults :count="result?.customerPaginated.data.length">

                    <div>

                        <Table :skeleton-columns="7">
                            <template #head>
                                <TableHeadSortable v-model="orderBy" columnName="ext_customer_id">Customer ID</TableHeadSortable>
                                <TableHeadSortable v-model="orderBy" columnName="ext_company_id">Company ID</TableHeadSortable>
                                <TableHeadSortable v-model="orderBy" columnName="billing_address->company">Billing Company</TableHeadSortable>
                                <TableHead>Discount Group</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHeadSortable v-model="orderBy" columnName="email">Email</TableHeadSortable>
                                <TableHead>Actions</TableHead>
                            </template>

                            <template #body>
                                <TableRow v-for="item in result.customerPaginated.data" :key="item.id">
                                    <TableColumn>{{ item.ext_customer_id }}</TableColumn>
                                    <TableColumn>{{ item.ext_company_id }}</TableColumn>
                                    <TableColumn>{{ item.billing_address?.company }}</TableColumn>
                                    <TableColumn>{{ item.discountGroup?.name }}</TableColumn>
                                    <TableColumn>{{ item.first_name }} {{ item.last_name }}</TableColumn>
                                    <TableColumn>{{ item.email }}</TableColumn>

                                    <TableColumn class="w-[20%]">
                                        <Button class="small" @click="select(item, closeModal)">
                                            <TextWithIcon icon="pen">Select</TextWithIcon>
                                        </Button>
                                    </TableColumn>
                                </TableRow>
                            </template>
                        </Table>

                    </div>

                    <div v-if="result?.customerPaginated.paginatorInfo" class="flex justify-between my-4">
                        <Pagination
                            @pageChange="(selectedPage) => page = selectedPage"
                            :paginatorInfo="result?.customerPaginated.paginatorInfo"
                        ></Pagination>

                        <PageItems
                            @change="page = 1"
                            v-model="count"
                        ></PageItems>
                    </div>

                </HasResults>

            </Box>

            <div class="flex justify-between">
                <Button @click="closeModal">Cancel</Button>
            </div>

        </template>

    </ModalButton>



</template>

<script setup>
import { ref, onMounted, computed, watch, provide } from 'vue'

import { useGlobals, useLazyQuery } from "@/composables/useGlobals.js";

const {
    cloneDeep,
    gql
} = useGlobals()

const props     = defineProps({
    buttonLabel     : String
})

const currentFilters = ref({
    search_text         : '',
    discount_group_id   : null,
})

const emit              = defineEmits([
    'selected'
])


const count     = ref(10)
const page      = ref(1)
const filters   = ref(cloneDeep(currentFilters.value))
const orderBy   = ref({ column: 'email', order: 'ASC' })

const { load, result, loading, error } = useLazyQuery(gql`
    query ($first: Int!, $page: Int, $filters: CustomerFilterInput, $orderBy: OrderByClause) {

        customerDiscountGroupFilter {
            id
            count
            name
        }

        customerPaginated(first: $first, page: $page, filters: $filters, orderBy: $orderBy) {
            paginatorInfo {
                ...PaginatorInfoFragment
            }
            data {
                ...CustomerFragment
            }
        }
    }
`, {
    first   : count,
    page    : page,
    filters : filters,
    orderBy : orderBy
}, {
    fetchPolicy: 'cache-and-network'
})

const onModalEnter      = async () => {
    await load()
}

const select        = (item, closeModalCallback) => {

    emit('selected', item)

    closeModalCallback()
}

const resetFilters  = () => {

    currentFilters.value        = {
        search_text     : ''
    }

    filters.value   = cloneDeep(currentFilters.value)

}

const search        = () => {
    page.value      = 1
    filters.value   = cloneDeep(currentFilters.value)
}

provide("isLoading", loading)
</script>